import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';
import {
  Briefcase, MapPin, Clock, Users, ArrowRight, CheckCircle,
  Loader2, Upload, Sparkles, Heart, Globe, Rocket, Send,
  Building2, Code, Video, ChevronRight, Star
} from 'lucide-react';

const Careers = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplication, setShowApplication] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  
  const [formData, setFormData] = useState({
    job_id: jobId || null,
    full_name: '',
    email: '',
    phone: '',
    linkedin: '',
    portfolio: '',
    skills: [],
    experience_years: 0,
    current_role: '',
    why_wayo: '',
    how_contribute: '',
    additional_info: ''
  });
  
  const [skillInput, setSkillInput] = useState('');
  const [resumeFile, setResumeFile] = useState(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (jobId && jobs.length > 0) {
      const job = jobs.find(j => j.id === jobId);
      setSelectedJob(job);
      setFormData(prev => ({ ...prev, job_id: jobId }));
    }
  }, [jobId, jobs]);

  const fetchJobs = async () => {
    try {
      const response = await api.get('/careers/jobs');
      setJobs(response.data.jobs);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.why_wayo || !formData.how_contribute) {
      toast({ title: "Required Fields", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    
    if (formData.skills.length === 0) {
      toast({ title: "Skills Required", description: "Please add at least one skill", variant: "destructive" });
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.post('/careers/apply', formData);
      setApplicationId(response.data.application_id);
      
      // Upload resume if provided
      if (resumeFile && response.data.application_id) {
        const formDataFile = new FormData();
        formDataFile.append('file', resumeFile);
        await api.post(`/careers/apply/${response.data.application_id}/resume`, formDataFile, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast({ title: "Application Submitted!", description: "We'll review your application and get back to you soon." });
      setShowApplication(false);
      
      // Reset form
      setFormData({
        job_id: null,
        full_name: '',
        email: '',
        phone: '',
        linkedin: '',
        portfolio: '',
        skills: [],
        experience_years: 0,
        current_role: '',
        why_wayo: '',
        how_contribute: '',
        additional_info: ''
      });
      setResumeFile(null);
      
    } catch (error) {
      toast({ title: "Error", description: "Failed to submit application", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const departmentIcons = {
    Engineering: <Code className="h-5 w-5" />,
    Marketing: <Video className="h-5 w-5" />
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Job Detail View
  if (selectedJob) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Hero */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <button 
              onClick={() => { setSelectedJob(null); navigate('/careers'); }}
              className="text-white/80 hover:text-white mb-4 flex items-center gap-1"
            >
              ← Back to all jobs
            </button>
            <div className="flex items-center gap-3 mb-4">
              <Badge className="bg-white/20">{selectedJob.department}</Badge>
              <Badge className="bg-white/20">{selectedJob.type}</Badge>
            </div>
            <h1 className="text-4xl font-bold mb-4">{selectedJob.title}</h1>
            <div className="flex flex-wrap gap-4 text-white/90">
              <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {selectedJob.location}</span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {selectedJob.experience}</span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="md:col-span-2 space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle>About the Role</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 whitespace-pre-line">{selectedJob.description}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Rocket className="h-5 w-5 text-emerald-600" />
                    What You'll Do
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {selectedJob.responsibilities.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-yellow-500" />
                    You're a Great Fit If You
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {selectedJob.requirements.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <ChevronRight className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {selectedJob.bonus?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-purple-500" />
                      Bonus Points For
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {selectedJob.bonus.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-600">
                          <span className="text-purple-500">+</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Heart className="h-5 w-5 text-red-500" />
                    Why Wayo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {selectedJob.benefits.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              <Card className="sticky top-4">
                <CardContent className="pt-6">
                  <Button 
                    onClick={() => setShowApplication(true)}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 mb-4"
                    size="lg"
                  >
                    Apply Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Department</span>
                      <span className="font-medium">{selectedJob.department}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Location</span>
                      <span className="font-medium">{selectedJob.location}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Type</span>
                      <span className="font-medium">{selectedJob.type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Experience</span>
                      <span className="font-medium">{selectedJob.experience}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Application Modal for Job Detail View */}
        {showApplication && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="application-modal">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span data-testid="application-modal-title">{formData.job_id ? `Apply for ${jobs.find(j => j.id === formData.job_id)?.title || selectedJob?.title}` : 'Open Application'}</span>
                  <button onClick={() => setShowApplication(false)} className="text-gray-400 hover:text-gray-600" data-testid="close-modal-btn">✕</button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Full Name *</label>
                      <Input
                        required
                        value={formData.full_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                        placeholder="John Doe"
                        data-testid="input-full-name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Email *</label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="john@example.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Phone</label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">LinkedIn</label>
                      <Input
                        value={formData.linkedin}
                        onChange={(e) => setFormData(prev => ({ ...prev, linkedin: e.target.value }))}
                        placeholder="linkedin.com/in/johndoe"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Portfolio / GitHub</label>
                      <Input
                        value={formData.portfolio}
                        onChange={(e) => setFormData(prev => ({ ...prev, portfolio: e.target.value }))}
                        placeholder="github.com/johndoe"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Years of Experience</label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.experience_years}
                        onChange={(e) => setFormData(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Current Role</label>
                    <Input
                      value={formData.current_role}
                      onChange={(e) => setFormData(prev => ({ ...prev, current_role: e.target.value }))}
                      placeholder="Software Engineer at Company X"
                    />
                  </div>

                  {/* Skills */}
                  <div>
                    <label className="text-sm font-medium">Skills *</label>
                    <div className="flex gap-2 mb-2">
                      <Input
                        value={skillInput}
                        onChange={(e) => setSkillInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                        placeholder="Add a skill and press Enter"
                        data-testid="input-skill"
                      />
                      <Button type="button" onClick={addSkill} variant="outline" data-testid="add-skill-btn">Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.skills.map((skill) => (
                        <Badge key={skill} className="px-3 py-1">
                          {skill}
                          <button type="button" onClick={() => removeSkill(skill)} className="ml-2 text-xs">✕</button>
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Why Wayo */}
                  <div>
                    <label className="text-sm font-medium">Why do you want to join Wayo? *</label>
                    <Textarea
                      required
                      rows={4}
                      value={formData.why_wayo}
                      onChange={(e) => setFormData(prev => ({ ...prev, why_wayo: e.target.value }))}
                      placeholder="Tell us what excites you about Wayo and our mission..."
                      data-testid="input-why-wayo"
                    />
                  </div>

                  {/* How Contribute */}
                  <div>
                    <label className="text-sm font-medium">How can you make a change at Wayo? *</label>
                    <Textarea
                      required
                      rows={4}
                      value={formData.how_contribute}
                      onChange={(e) => setFormData(prev => ({ ...prev, how_contribute: e.target.value }))}
                      placeholder="Tell us about your unique skills and how you can contribute to our team..."
                      data-testid="input-how-contribute"
                    />
                  </div>

                  {/* Resume Upload */}
                  <div>
                    <label className="text-sm font-medium">Resume (PDF or Word)</label>
                    <div className="mt-1">
                      <label className="flex items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                        <div className="text-center">
                          <Upload className="h-6 w-6 mx-auto text-gray-400 mb-1" />
                          <span className="text-sm text-gray-500">
                            {resumeFile ? resumeFile.name : 'Click to upload resume'}
                          </span>
                        </div>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                          onChange={(e) => setResumeFile(e.target.files[0])}
                          data-testid="input-resume"
                        />
                      </label>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div>
                    <label className="text-sm font-medium">Anything else you'd like us to know?</label>
                    <Textarea
                      rows={3}
                      value={formData.additional_info}
                      onChange={(e) => setFormData(prev => ({ ...prev, additional_info: e.target.value }))}
                      placeholder="Portfolio links, availability, salary expectations, etc."
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => setShowApplication(false)} className="flex-1" data-testid="cancel-application-btn">
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500" data-testid="submit-application-btn">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Submit Application
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  // Main Careers Page
  return (
    <div className="min-h-screen bg-gray-50" data-testid="careers-page">
      {/* Hero */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white py-20">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-6">
            <Users className="h-8 w-8" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="careers-hero-title">Join the Wayo Team</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
            Help us transform how international students navigate their journey to US colleges. 
            We're building something meaningful — and we want you to be part of it.
          </p>
          <Button 
            onClick={() => { setFormData(prev => ({ ...prev, job_id: null })); setShowApplication(true); }}
            size="lg"
            className="bg-white text-emerald-600 hover:bg-gray-100"
            data-testid="open-application-btn"
          >
            <Sparkles className="mr-2 h-5 w-5" />
            Submit Open Application
          </Button>
        </div>
      </div>

      {/* Values */}
      <div className="max-w-5xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-center mb-12">Why Work at Wayo?</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="text-center p-6">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="h-6 w-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold mb-2">Mission-Driven</h3>
            <p className="text-gray-600 text-sm">Every feature we build helps students achieve their dreams of studying abroad.</p>
          </Card>
          <Card className="text-center p-6">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="font-semibold mb-2">Remote-First</h3>
            <p className="text-gray-600 text-sm">Work from anywhere. We value results over presence, and trust over micromanagement.</p>
          </Card>
          <Card className="text-center p-6">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Rocket className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="font-semibold mb-2">Early Stage</h3>
            <p className="text-gray-600 text-sm">Join early and grow with us. Your impact will be visible and your voice will be heard.</p>
          </Card>
        </div>
      </div>

      {/* Open Positions */}
      <div className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8">Open Positions</h2>
          
          {jobs.length > 0 ? (
            <div className="space-y-4" data-testid="job-listings">
              {jobs.map((job) => (
                <Card 
                  key={job.id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => { setSelectedJob(job); navigate(`/careers/${job.id}`); }}
                  data-testid={`job-card-${job.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                          {departmentIcons[job.department] || <Briefcase className="h-6 w-6 text-emerald-600" />}
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold mb-1">{job.title}</h3>
                          <div className="flex flex-wrap gap-2 mb-2">
                            <Badge variant="secondary">{job.department}</Badge>
                            <Badge variant="outline">{job.type}</Badge>
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {job.location}</span>
                            <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {job.experience}</span>
                          </div>
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="text-center py-12">
              <p className="text-gray-500">No open positions at the moment. Submit an open application below!</p>
            </Card>
          )}
        </div>
      </div>

      {/* Open Application CTA */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Don't See Your Role?</h2>
          <p className="text-white/90 mb-8">
            We're always looking for talented people who share our mission. 
            Tell us about yourself and how you can contribute to Wayo.
          </p>
          <Button 
            onClick={() => { setFormData(prev => ({ ...prev, job_id: null })); setShowApplication(true); }}
            size="lg"
            className="bg-white text-purple-600 hover:bg-gray-100"
          >
            Submit Open Application
            <Send className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Admin Link - Small footer */}
      <div className="bg-gray-100 py-4">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <Link 
            to="/careers/admin" 
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="admin-link"
          >
            Admin Portal
          </Link>
        </div>
      </div>

      {/* Application Modal */}
      {showApplication && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="application-modal">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span data-testid="application-modal-title">{formData.job_id ? `Apply for ${jobs.find(j => j.id === formData.job_id)?.title}` : 'Open Application'}</span>
                <button onClick={() => setShowApplication(false)} className="text-gray-400 hover:text-gray-600" data-testid="close-modal-btn">✕</button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Full Name *</label>
                    <Input
                      required
                      value={formData.full_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                      placeholder="John Doe"
                      data-testid="input-full-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email *</label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="john@example.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Phone</label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+1 (555) 123-4567"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">LinkedIn</label>
                    <Input
                      value={formData.linkedin}
                      onChange={(e) => setFormData(prev => ({ ...prev, linkedin: e.target.value }))}
                      placeholder="linkedin.com/in/johndoe"
                      data-testid="input-linkedin"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Portfolio / GitHub</label>
                    <Input
                      value={formData.portfolio}
                      onChange={(e) => setFormData(prev => ({ ...prev, portfolio: e.target.value }))}
                      placeholder="github.com/johndoe"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Years of Experience</label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.experience_years}
                      onChange={(e) => setFormData(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium">Current Role</label>
                  <Input
                    value={formData.current_role}
                    onChange={(e) => setFormData(prev => ({ ...prev, current_role: e.target.value }))}
                    placeholder="Software Engineer at Company X"
                  />
                </div>

                {/* Skills */}
                <div>
                  <label className="text-sm font-medium">Skills *</label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                      placeholder="Add a skill and press Enter"
                    />
                    <Button type="button" onClick={addSkill} variant="outline">Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.skills.map((skill) => (
                      <Badge key={skill} className="px-3 py-1">
                        {skill}
                        <button type="button" onClick={() => removeSkill(skill)} className="ml-2 text-xs">✕</button>
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Why Wayo */}
                <div>
                  <label className="text-sm font-medium">Why do you want to join Wayo? *</label>
                  <Textarea
                    required
                    rows={4}
                    value={formData.why_wayo}
                    onChange={(e) => setFormData(prev => ({ ...prev, why_wayo: e.target.value }))}
                    placeholder="Tell us what excites you about Wayo and our mission..."
                  />
                </div>

                {/* How Contribute */}
                <div>
                  <label className="text-sm font-medium">How can you make a change at Wayo? *</label>
                  <Textarea
                    required
                    rows={4}
                    value={formData.how_contribute}
                    onChange={(e) => setFormData(prev => ({ ...prev, how_contribute: e.target.value }))}
                    placeholder="Tell us about your unique skills and how you can contribute to our team..."
                  />
                </div>

                {/* Resume Upload */}
                <div>
                  <label className="text-sm font-medium">Resume (PDF or Word)</label>
                  <div className="mt-1">
                    <label className="flex items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                      <div className="text-center">
                        <Upload className="h-6 w-6 mx-auto text-gray-400 mb-1" />
                        <span className="text-sm text-gray-500">
                          {resumeFile ? resumeFile.name : 'Click to upload resume'}
                        </span>
                      </div>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        onChange={(e) => setResumeFile(e.target.files[0])}
                      />
                    </label>
                  </div>
                </div>

                {/* Additional Info */}
                <div>
                  <label className="text-sm font-medium">Anything else you'd like us to know?</label>
                  <Textarea
                    rows={3}
                    value={formData.additional_info}
                    onChange={(e) => setFormData(prev => ({ ...prev, additional_info: e.target.value }))}
                    placeholder="Portfolio links, availability, salary expectations, etc."
                  />
                </div>

                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowApplication(false)} className="flex-1" data-testid="cancel-application-btn">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500" data-testid="submit-application-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Submit Application
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Careers;
